import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { SystemTurn, UserTurn, FeedbackProps } from "@tomis-tech/chat-ui";

import { getOrCreateChatSession } from "@/session";
import {
  ChatWidgetConfiguration,
  ChatWidgetTurn,
  ChatWidgetHandoffStatus,
  FormInstance,
} from "@/types";
import { trackEvent } from "@/tracking";
import {
  convertApiTurnsToUITurns,
  convertWelcomeMessagesToTurns,
} from "@/utils";

interface UseRuntimeParams {
  /** Chat widget configuration */
  config: ChatWidgetConfiguration;
  /** Initial conversation history from load endpoint */
  initialConversationHistory?: ChatWidgetTurn[];
  /** Live-chat handoff state for the restored conversation, from load. When
   * `waiting`/`live` the widget starts polling immediately. */
  initialHandoffStatus?: ChatWidgetHandoffStatus;
  /** Site ID for storage and API calls */
  siteId: string;
  /** Initial conversation-level forms from load */
  initialForms?: FormInstance[];
  /** Feedback mode for system messages */
  feedbackMode?: FeedbackProps["mode"];
  /** Callback to handle errors (e.g., network errors) */
  onError?: (error: string) => void;
  /** Callback for successful actions (e.g., form submit confirmations) */
  onSuccess?: (message: string) => void;
}

interface RuntimeAPI {
  /** Send a message to the chatbot */
  sendMessage: (text: string) => Promise<boolean>;
  /** Restart the conversation (clears history) */
  restartSession: () => Promise<void>;
  /** Current conversation turns for rendering */
  turns: Array<SystemTurn | UserTurn>;
  /** Whether a message is currently being sent */
  isLoading: boolean;
  /** Current conversation-level forms */
  forms: FormInstance[];
  /** Currently open form key (if any) */
  activeFormKey: string | null;
  /** Open a form overlay by key */
  openForm: (key: string) => Promise<void>;
  /** Close the active form overlay */
  closeForm: () => void;
  /** Update current form values (optimistic) */
  updateFormValues: (patch: Record<string, any>) => void;
  /** Submit current form values via /message endpoint */
  submitForm: () => Promise<boolean>;
  /** Current bot-pause / human-takeover state for the conversation. */
  handoffStatus: ChatWidgetHandoffStatus;
  /** Whether a live operator is currently typing (from the poll endpoint). */
  agentTyping: boolean;
  /** Publish a (throttled) customer-typing ping; no-op outside live chat. */
  notifyTyping: () => void;
}

/**
 * Hook that subscribes this React tree to the shared chat session.
 *
 * Conversation state, the live-chat poller, and typing presence live in a
 * module-level store keyed by `siteId` (`getOrCreateChatSession`) so a later
 * second mount can share one conversation. Per-instance UI (open/hidden/
 * proactive) stays in `useChatWidget`.
 *
 * This hook is specifically for CHAT mode. For form-only mode, use useFormOnly.
 */
export const useRuntime = ({
  config,
  initialConversationHistory = [],
  initialHandoffStatus = "bot",
  siteId,
  initialForms = [],
  feedbackMode = "disabled",
  onError,
  onSuccess,
}: UseRuntimeParams): RuntimeAPI => {
  const session = getOrCreateChatSession(siteId, {
    conversationHistory: initialConversationHistory,
    handoffStatus: initialHandoffStatus,
    forms: initialForms,
  });

  const [snapshot, setSnapshot] = useState(() => session.getState());

  const onErrorRef = useRef(onError);
  const onSuccessRef = useRef(onSuccess);
  onErrorRef.current = onError;
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    const unsubscribe = session.subscribe(() => {
      setSnapshot(session.getState());
    });
    const unregisterNotices = session.addNoticeListener({
      onError: (message) => onErrorRef.current?.(message),
      onSuccess: (message) => onSuccessRef.current?.(message),
    });
    setSnapshot(session.getState());
    return () => {
      unsubscribe();
      unregisterNotices();
    };
  }, [session]);

  const handleAction = useCallback(
    async (action: {
      type: string;
      name: string;
      key?: string;
      faq_id?: number;
      welcome_button_id?: number;
    }) => {
      if (action.type === "form" && action.key) {
        session.openForm(action.key);
        return;
      }
      trackEvent("clicked_quick_reply", {
        button_name: action.name,
        ...(action.welcome_button_id !== undefined
          ? { welcome_button_id: action.welcome_button_id }
          : {}),
        ...(action.faq_id !== undefined ? { faq_id: action.faq_id } : {}),
      });
      await session.sendMessage(
        action.name,
        action.faq_id,
        action.welcome_button_id,
        true,
      );
    },
    [session],
  );

  const handleLinkClick = useCallback((href: string, turnId?: string) => {
    const turn = turnId ? { turn_id: turnId } : {};
    if (href.startsWith("tel:")) {
      trackEvent("clicked_phone", turn);
    } else if (href.startsWith("mailto:")) {
      trackEvent("clicked_email", turn);
    } else if (href.startsWith("http://") || href.startsWith("https://")) {
      trackEvent("clicked_url", { url: href, ...turn });
    }
  }, []);

  const convertToTurns = useCallback(
    (apiTurns: ChatWidgetTurn[]): Array<SystemTurn | UserTurn> => {
      return convertApiTurnsToUITurns(
        apiTurns,
        config.image,
        handleAction,
        (turn): FeedbackProps | undefined => {
          if (turn.type !== "system") return undefined;
          if (!feedbackMode || feedbackMode === "disabled") return undefined;

          const fb = turn.feedback;
          const data = fb
            ? {
                rating: (fb.rating ?? undefined) as
                  | "positive"
                  | "negative"
                  | undefined,
                message: fb.message ?? undefined,
                createdAt: fb.createdAt ? new Date(fb.createdAt) : undefined,
                createdBy: fb.createdByName || fb.createdBy || undefined,
                createdByTomisId:
                  fb.createdByTomisId !== null &&
                  fb.createdByTomisId !== undefined
                    ? String(fb.createdByTomisId)
                    : undefined,
                createdByEmail: fb.createdByEmail ?? undefined,
                resolved: fb.resolved ?? false,
                resolvedAt: fb.resolvedAt ? new Date(fb.resolvedAt) : undefined,
                resolvedBy: fb.resolvedByName || fb.resolvedBy || undefined,
                resolvedByTomisId:
                  fb.resolvedByTomisId !== null &&
                  fb.resolvedByTomisId !== undefined
                    ? String(fb.resolvedByTomisId)
                    : undefined,
              }
            : undefined;

          if (feedbackMode === "view") {
            return { mode: "view", data };
          }
          if (feedbackMode === "review") {
            return { mode: "review", data };
          }
          return {
            mode: "edit",
            data,
            onSaveRating: (rating) =>
              session.upsertTurnFeedback(turn.id, { rating: rating ?? null }),
            onSaveFeedback: (text: string) =>
              session.upsertTurnFeedback(turn.id, { message: text }),
          };
        },
        handleLinkClick,
      );
    },
    [config.image, handleAction, feedbackMode, handleLinkClick, session],
  );

  const welcomeTurns = useMemo((): Array<SystemTurn> => {
    return convertWelcomeMessagesToTurns(
      config.welcomeMessages || [],
      config.image,
      handleAction,
      handleLinkClick,
    );
  }, [config.welcomeMessages, config.image, handleAction, handleLinkClick]);

  const turns = useMemo((): Array<SystemTurn | UserTurn> => {
    if (
      snapshot.hasRealConversation &&
      snapshot.conversationHistory.length > 0
    ) {
      return convertToTurns(snapshot.conversationHistory);
    }

    if (
      !snapshot.hasRealConversation &&
      snapshot.conversationHistory.length > 0
    ) {
      const optimisticUserTurns = convertToTurns(snapshot.conversationHistory);
      return [...welcomeTurns, ...optimisticUserTurns];
    }

    return welcomeTurns;
  }, [
    snapshot.conversationHistory,
    snapshot.hasRealConversation,
    convertToTurns,
    welcomeTurns,
  ]);

  const openForm = useCallback(
    async (key: string) => {
      session.openForm(key);
    },
    [session],
  );

  return {
    sendMessage: session.sendMessage,
    restartSession: session.restartSession,
    turns,
    isLoading: snapshot.isSendingMessage,
    forms: snapshot.forms,
    activeFormKey: snapshot.activeFormKey,
    openForm,
    closeForm: session.closeForm,
    updateFormValues: session.updateFormValues,
    submitForm: session.submitForm,
    handoffStatus: snapshot.handoffStatus,
    agentTyping: snapshot.agentTyping,
    notifyTyping: session.notifyTyping,
  };
};
